/**
 * AwarenessPanel.tsx
 *
 * Left panel with two tabs:
 *   对话 — Collaborative AI chat (Yjs-backed). Model selector above input.
 *          File upload → /api/upload → content injected into AI context.
 *          AI streams via SSE /api/chat. FIELD_UPDATE tags → Yjs + highlight.
 *   动态 — Activity log: join/leave, uploads, AI fills, saves.
 *
 * Input area (model selector + input + toolbar) is ALWAYS rendered
 * regardless of active tab, per UX requirement.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudio, useYArray } from "./StudioContext.js";
import { makeField, type BomRow } from "../schema.js";
import { supabase, getFreshToken } from "./auth.js";
import { FolderActivityStream } from "./folder_activity_stream.js";

// ---------------------------------------------------------------------------
// Model definitions (mirrors SpecFlow's MODEL_OPTIONS)
// ---------------------------------------------------------------------------

const CHAT_MODELS = [
  { id: "gemini-pro",    label: "Gemini 2.5 Pro",    shortLabel: "GEMINI 2.5 PRO",   byok: false },
  { id: "gemini-flash",  label: "Gemini 2.5 Flash",  shortLabel: "GEMINI 2.5 FLASH", byok: false },
  { id: "claude-sonnet", label: "Claude Sonnet",      shortLabel: "CLAUDE SONNET",    byok: true  },
  { id: "kimi",          label: "Kimi 2.5 (中国区)",  shortLabel: "KIMI 2.5",         byok: true  },
  { id: "glm4",          label: "GLM-4 (中国区)",     shortLabel: "GLM-4",            byok: false },
  { id: "mistral",       label: "Mistral Large (EU)", shortLabel: "MISTRAL LARGE",    byok: false },
] as const;

type ChatModelId = typeof CHAT_MODELS[number]["id"];

// ---------------------------------------------------------------------------
// Model health
// ---------------------------------------------------------------------------

interface ModelStatusEntry { status: "ok" | "degraded" | "down"; successRate: number; lastAt: number }
type ModelHealth = Record<string, ModelStatusEntry>;

function healthDotColor(health: ModelHealth, modelId: string): string {
  const s = health[modelId]?.status;
  return s === "down" ? "#ef4444" : s === "degraded" ? "#f59e0b" : "#22c55e";
}

// First healthy model in the preferred fallback order
const _GLOBAL_FALLBACK_ORDER: ChatModelId[] = ["gemini-flash", "glm4", "mistral", "gemini-pro", "kimi"];
function findHealthyModel(health: ModelHealth, current: ChatModelId): ChatModelId | null {
  return _GLOBAL_FALLBACK_ORDER.find(id => id !== current && (health[id]?.status ?? "ok") !== "down") ?? null;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMsg {
  id: string;
  authorId: string;
  authorName: string;
  authorColor?: string;  // durable color from DB; fallback to awareness lookup if absent
  text: string;
  ts: number;
  msgType?: "text" | "file";
  fileName?: string;
  fileType?: string;
  layer?: string;
}

interface FileCtx {
  fileName: string;
  fileType: string;
  extractedText: string;
  uploadedAt: number;
  type?: "text" | "bom" | "image" | "techpack_json";
  bomRows?: Record<string, unknown>[];
  base64?: string;
  mimeType?: string;
  data?: unknown;
}

interface ActivityEntry {
  id: string;
  icon: string;
  text: string;
  ts: number;
}

type Tab = "chat" | "activity";

const TAB_L: Record<Tab, Record<string, string>> = {
  chat:     { en:"Chat",     fr:"Chat",      it:"Chat",       zh:"对话" },
  activity: { en:"Activity", fr:"Activité",  it:"Attività",   zh:"动态" },
};
function tabLabel(tab: Tab, lang: string): string {
  return TAB_L[tab]?.[lang] ?? TAB_L[tab]?.en ?? tab;
}

// Fields AI can write via <FIELD_UPDATE> tags
const METADATA_FIELDS = new Set([
  "style_number", "season", "date", "description", "product_category", "colorway", "project_name", "version",
]);
const MATERIAL_FIELDS = new Set(["fabric", "trim", "accessories"]);
const CONSTRUCTION_FIELDS = new Set([
  "seam_allowance", "tolerance", "production_type", "special_techniques",
  "hardware_trim", "additional_notes", "factory_notes",
]);
const MEASUREMENT_KEYS = new Set([
  "A_front_length", "B_back_length", "C_shoulder", "D_chest", "E_waist", "F_hip", "G_hem",
  "H_sleeve_length", "I_sleeve_width", "J_cuff", "K_collar_width", "L_waist_height",
  "M_front_rise", "N_back_rise",
]);

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  style_number: "款号", season: "季节", date: "日期", description: "描述",
  product_category: "品类", colorway: "配色", project_name: "项目名", version: "版本",
  fabric: "面料", trim: "辅料", accessories: "配件",
  seam_allowance: "缝份", tolerance: "公差", production_type: "生产类型",
  special_techniques: "特殊工艺", hardware_trim: "五金辅料",
  additional_notes: "附加备注", factory_notes: "工厂备注",
  A_front_length: "前长", B_back_length: "后长", C_shoulder: "肩宽",
  D_chest: "胸围", E_waist: "腰围", F_hip: "臀围", G_hem: "下摆",
  H_sleeve_length: "袖长", I_sleeve_width: "袖宽", J_cuff: "袖口",
  K_collar_width: "领宽", L_waist_height: "腰高", M_front_rise: "前浪", N_back_rise: "后浪",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripFieldTags(text: string): string {
  return text.replace(/<FIELD_UPDATE>[\s\S]*?<\/FIELD_UPDATE>/g, "").trim();
}

function cleanMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/^\|[-: |]+\|$/gm, "")
    .replace(/\|/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function fmtRelTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "刚刚";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}分钟前`;
  const d = new Date(ts);
  if (d.toDateString() === new Date().toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function flashField(fieldKey: string): void {
  window.dispatchEvent(new CustomEvent("sf:highlight-field", { detail: { field: fieldKey } }));
}

function fileIcon(fileType?: string, fileName?: string): string {
  const name = (fileName ?? "").toLowerCase();
  if (!fileType && !name) return "📄";
  if ((fileType ?? "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/.test(name)) return "🖼️";
  if (name.endsWith(".pdf") || fileType === "application/pdf") return "📕";
  if (name.endsWith(".json") || fileType === "application/json") return "📋";
  if (/\.xlsx?$/.test(name) || (fileType ?? "").includes("spreadsheet")) return "📊";
  return "📎";
}

function parseFieldUpdates(raw: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of raw.split("\n")) {
    const m = line.match(/^\*\*([^*]+)\*\*[:\s]+(.+)$/);
    if (m) result[m[1].trim()] = m[2].trim();
  }
  return result;
}

// ---------------------------------------------------------------------------
// AwarenessPanel
// ---------------------------------------------------------------------------

export default function AwarenessPanel() {
  const { techPack, userId, userName, userColor, userTimezone, languageCode, awarenessUsers, roomId, syncState } = useStudio();

  // Yjs-backed chat messages (collaborative across all users in room)
  const chatArr     = useMemo(() => techPack.ydoc.getArray<ChatMsg>("__studio_chat__"), [techPack]);
  const yjsMessages = useYArray<ChatMsg>(chatArr) as readonly ChatMsg[];

  const imgCacheRef = useRef<Map<string, string>>(new Map());
  const [, forceRender] = useState(0);

  const [cachedMessages, setCachedMessages] = useState<ChatMsg[]>(() => {
    try {
      const raw = localStorage.getItem(`studio_chat_${roomId}`);
      return raw ? (JSON.parse(raw) as ChatMsg[]) : [];
    } catch { return []; }
  });

  const messages = yjsMessages.length > 0 ? yjsMessages : cachedMessages;

  // Local state
  const [activeTab,   setActiveTab]  = useState<Tab>("chat");
  const [draft,       setDraft]      = useState("");
  const [streaming,   setStreaming]  = useState(false);
  const [streamText,  setStreamText] = useState("");
  const [error,       setError]      = useState<string | null>(null);
  const [fileContext, setFileCtx]    = useState<FileCtx | null>(null);
  const [chatModel,   setChatModel]  = useState<ChatModelId>(
    () => (localStorage.getItem("studio_chat_model") as ChatModelId) ?? "gemini-pro",
  );
  const [modelHealth,   setModelHealth]   = useState<ModelHealth>({});
  const [failedMsgIds, setFailedMsgIds] = useState<Set<string>>(new Set());
  const chatModelRef = useRef<ChatModelId>(chatModel);
  useEffect(() => { chatModelRef.current = chatModel; }, [chatModel]);

  const actArr         = useMemo(() => techPack.ydoc.getArray<ActivityEntry>("__studio_activity__"), [techPack]);
  const yjsActivityLog = useYArray<ActivityEntry>(actArr) as readonly ActivityEntry[];
  const [localActivityLog, setLocalActivityLog] = useState<readonly ActivityEntry[]>([]);
  const activityLog = useMemo(
    () => [...yjsActivityLog, ...localActivityLog].sort((a, b) => a.ts - b.ts),
    [yjsActivityLog, localActivityLog],
  );

  const bottomRef    = useRef<HTMLDivElement>(null);
  const actBottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef     = useRef<AbortController | null>(null);
  const prevUserIds  = useRef<Set<string> | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, streamText]);

  useEffect(() => {
    actBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activityLog.length]);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const pushMsg = useCallback((msg: ChatMsg) => { chatArr.push([msg]); }, [chatArr]);

  // Persist a human message to DB (Change 1: not fire-and-forget).
  // Retries once on failure. On second failure: marks the message with an
  // unsaved indicator + retry affordance so the user knows data is at risk.
  const persistMessage = useCallback(async (msgId: string, text: string, isAi = false) => {
    if (!roomId) return;
    const token = await getFreshToken() ?? "";
    const doPost = () => fetch(`/api/studio/techpacks/${roomId}/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text, layer: "collab", avatarColor: userColor, isAi }),
    });
    try {
      let r = await doPost();
      if (!r.ok) {
        await new Promise(res => setTimeout(res, 1500));
        r = await doPost();
      }
      if (r.ok) {
        setFailedMsgIds(prev => { const next = new Set(prev); next.delete(msgId); return next; });
      } else {
        setFailedMsgIds(prev => new Set([...prev, msgId]));
      }
    } catch {
      setFailedMsgIds(prev => new Set([...prev, msgId]));
    }
  }, [roomId, userColor]);

  // Model health polling: check on mount + every 5 minutes
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch("/api/model-status");
        if (!res.ok) return;
        const health = await res.json() as ModelHealth;
        setModelHealth(health);

        // Auto-switch if current model is down
        const current = chatModelRef.current;
        if (health[current]?.status === "down") {
          const fallback = findHealthyModel(health, current);
          if (fallback) {
            setChatModel(fallback);
            localStorage.setItem("studio_chat_model", fallback);
            const fromLabel = CHAT_MODELS.find(m => m.id === current)?.label ?? current;
            const toLabel   = CHAT_MODELS.find(m => m.id === fallback)?.label ?? fallback;
            pushMsg({
              id: `sys-fallback-${Date.now()}`,
              authorId: "ai-assistant", authorName: "AI",
              text: `⚠️ ${fromLabel} 当前不可用，已自动切换到 ${toLabel}`,
              ts: Date.now(), msgType: "text",
            });
          }
        }
      } catch { /* network error — keep current health state */ }
    };
    void check();
    const interval = setInterval(() => void check(), 5 * 60_000);
    return () => clearInterval(interval);
  }, [pushMsg]); // pushMsg is stable (useCallback)

  useEffect(() => {
    if (yjsMessages.length > 0) {
      localStorage.setItem(`studio_chat_${roomId}`, JSON.stringify(yjsMessages.slice(-100)));
    }
  }, [yjsMessages, roomId]);

  useEffect(() => {
    if (syncState === "synced") {
      setCachedMessages([]);
      localStorage.removeItem(`studio_chat_${roomId}`);
    }
  }, [syncState, roomId]);

  const addActivity = useCallback((entry: Omit<ActivityEntry, "id">, writeToYjs = true) => {
    const act: ActivityEntry = { ...entry, id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}` };
    if (writeToYjs) {
      techPack.ydoc.transact(() => {
        techPack.ydoc.getArray<ActivityEntry>("__studio_activity__").push([act]);
      });
    } else {
      setLocalActivityLog(prev => [...prev.slice(-199), act]);
    }
  }, [techPack]);

  // Awareness join/leave → activity log
  useEffect(() => {
    const live = awarenessUsers.filter(u => !u.fadingOut);
    const cur  = new Set(live.map(u => u.userId));
    const prev = prevUserIds.current;
    if (prev === null) { prevUserIds.current = cur; return; }
    const now = Date.now();
    for (const id of cur) {
      if (!prev.has(id)) {
        const u = awarenessUsers.find(u => u.userId === id);
        // IP boundary: never show a UUID fragment as a human label.
        addActivity({ icon: "👤", text: `${u?.userName ?? "User"} 加入了`, ts: now }, false);
      }
    }
    for (const id of prev) {
      if (!cur.has(id)) {
        addActivity({ icon: "👤", text: `User 离开了`, ts: now }, false);
      }
    }
    prevUserIds.current = cur;
  }, [awarenessUsers, addActivity]);

  const applyFieldUpdates = useCallback((text: string): string[] => {
    console.log("[applyFieldUpdates] called, text.length:", text.length, "| has FIELD_UPDATE:", /<FIELD_UPDATE>/.test(text));
    const updatedFields: string[] = [];
    const regex = /<FIELD_UPDATE>([\s\S]*?)<\/FIELD_UPDATE>/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const updates = parseFieldUpdates(match[1]);
      for (const [rawKey, value] of Object.entries(updates)) {
        const field = rawKey.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
        console.log(`[FIELD_UPDATE] rawKey="${rawKey}" → field="${field}" value="${value}"`);
        const lang = languageCode ?? "en";
        techPack.ydoc.transact(() => {
          const env = makeField(value, lang, userId);
          if (METADATA_FIELDS.has(field))      techPack.metadata.set(field, env);
          else if (MATERIAL_FIELDS.has(field)) {
            const bomArray = techPack.bom;
            if (bomArray.length === 0) {
              bomArray.push([{ id: `bom-${Date.now()}`, component: "Main Body", material: value, composition: "", gsm: "", color_code: "", fabric_code: "", width: "", price: "" }]);
            } else {
              const firstRow = bomArray.get(0) as BomRow;
              bomArray.delete(0, 1);
              bomArray.insert(0, [{ ...firstRow, material: value }]);
            }
          }
          else if (CONSTRUCTION_FIELDS.has(field)) techPack.construction_details.set(field, env);
          else if (MEASUREMENT_KEYS.has(field)) techPack.measurements.set(field, env);
        });
        if (METADATA_FIELDS.has(field) || MATERIAL_FIELDS.has(field) || CONSTRUCTION_FIELDS.has(field) || MEASUREMENT_KEYS.has(field)) {
          flashField(field);
          addActivity({ icon: "🤖", text: `AI 填入 ${field}：${value}`, ts: Date.now() });
          updatedFields.push(field);
        }
      }
    }
    return updatedFields;
  }, [techPack, userId, languageCode, addActivity]);

  const sendToAI = useCallback(async (userText: string, overrideFileCtx?: FileCtx | null) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setStreaming(true);
    setStreamText("");
    setError(null);

    const history = [...messages]
      .filter(m => m.msgType !== "file")
      .slice(-10)
      .map(m => ({ role: m.authorId === "ai-assistant" ? "assistant" : "user", content: m.text }));
    history.push({ role: "user", content: userText });

    try {
      // Phase 3C item 2.4: the raw byokKey localStorage read is gone -- the
      // server now resolves a BYOK connection server-side (matched to the
      // model field's provider) for whichever real user the Authorization
      // header identifies. Also fixes a pre-existing bug found while
      // touching this call site: this previously sent the field as
      // `chatModel`, but server.ts's /api/chat handler reads `payload.model`
      // -- Tier 0's explicit model selection has never actually taken
      // effect from this caller.
      const ctxToSend = overrideFileCtx !== undefined ? overrideFileCtx : fileContext;
      const authToken = localStorage.getItem("studio_token") ?? "";
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          messages: history, lang: languageCode,
          model: chatModel, fileContext: ctxToSend,
        }),
        signal: abortRef.current.signal,
      });
      if (!res.ok || !res.body) {
        setError("AI unavailable — check your API key in Account settings");
        return;
      }
      const reader = res.body.getReader();
      const dec    = new TextDecoder();
      let buf = "", full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const ev = JSON.parse(data) as { text?: string; error?: string; modelFallback?: string };
            if (ev.error) { setError(ev.error); break; }
            if (ev.text)  { full += ev.text; setStreamText(full); }
            if (ev.modelFallback) {
              // Server fell back to next model — refresh health and note in chat
              void fetch("/api/model-status").then(r => r.ok ? r.json() as Promise<ModelHealth> : null)
                .then(h => { if (h) setModelHealth(h); }).catch(() => {});
            }
          } catch { /* skip */ }
        }
      }
      if (full) {
        console.log("[sendToAI] stream complete, full.length:", full.length, "| calling applyFieldUpdates");
        const updatedFields = applyFieldUpdates(full);
        const display = cleanMarkdown(stripFieldTags(full));
        if (display) {
          // Persist the substantive AI reply (collab layer, is_ai=true) with
          // the SAME retry + unsaved-indicator path as human messages (Change 1).
          // The field-confirmation toast below stays ephemeral — it's a future
          // execution-layer record (field_key/field_from/field_to), not chat.
          const aiMsgId = `ai-${Date.now()}`;
          pushMsg({ id: aiMsgId, authorId: "ai-assistant", authorName: "LanfinitasAI", text: display, ts: Date.now(), msgType: "text" });
          void persistMessage(aiMsgId, display, true);
        }
        if (updatedFields.length > 0) {
          const names = updatedFields.map(f => FIELD_DISPLAY_NAMES[f] ?? f).join("、");
          pushMsg({ id: `ai-fields-${Date.now()}`, authorId: "ai-assistant", authorName: "LanfinitasAI", text: `✓ 已更新 ${updatedFields.length} 个字段: ${names}`, ts: Date.now(), msgType: "text" });
        }
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") setError("Network error — please try again");
    } finally {
      setStreaming(false);
      setStreamText("");
    }
  }, [messages, languageCode, chatModel, fileContext, applyFieldUpdates, pushMsg, persistMessage]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (!text || streaming) return;
    const msgId = `u-${Date.now()}`;
    pushMsg({ id: msgId, authorId: userId, authorName: userName, authorColor: userColor, text, ts: Date.now(), msgType: "text" });
    setDraft("");
    void sendToAI(text);
    void persistMessage(msgId, text);
  }, [draft, streaming, pushMsg, userId, userName, userColor, sendToAI, persistMessage]);

  const compressImage = useCallback((dataUrl: string): Promise<string> => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        const maxPx = 800;
        if (w > maxPx || h > maxPx) {
          const ratio = Math.min(maxPx / w, maxPx / h);
          w = Math.round(w * ratio); h = Math.round(h * ratio);
        }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = () => resolve(dataUrl); // fallback: use original if compression fails
      img.src = dataUrl;
    });
  }, []);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = "";

    // Show file card in chat immediately
    const fileMsgId = `file-${Date.now()}`;
    const isImageFile = (file.type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(file.name);
    pushMsg({
      id: fileMsgId, authorId: userId, authorName: userName,
      text: file.name, ts: Date.now(), msgType: "file",
      fileName: file.name, fileType: file.type || (file.name.split(".").pop() ?? "binary"),
    });
    addActivity({ icon: "📎", text: `上传了 ${file.name}`, ts: Date.now() });

    // Upload to server for content extraction
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target?.result as string;
      if (isImageFile) {
        // Show original immediately for instant feedback
        imgCacheRef.current.set(fileMsgId, dataUrl);
        forceRender(n => n + 1);
        compressImage(dataUrl).then(async compressed => {
          // Update cache with compressed version for faster re-renders
          imgCacheRef.current.set(fileMsgId, compressed);
          forceRender(n => n + 1);
          // Upload to Supabase Storage — store URL in Yjs instead of base64
          // to avoid bloating the jsonb column (Storage handles binary efficiently)
          try {
            const blob = await fetch(compressed).then(r => r.blob());
            const filePath = `techpack-images/${roomId}/${fileMsgId}.jpg`;
            const { error: uploadErr } = await supabase.storage
              .from("studio-assets")
              .upload(filePath, blob, { contentType: "image/jpeg", upsert: true });
            if (uploadErr) throw uploadErr;
            const { data: { publicUrl } } = supabase.storage
              .from("studio-assets")
              .getPublicUrl(filePath);
            techPack.ydoc.transact(() => { techPack.images.set(`chat_img_${fileMsgId}`, publicUrl); });
          } catch (storageErr) {
            // Storage unavailable — fall back to storing compressed base64 in Yjs
            console.warn("[image] Storage upload failed, falling back to base64:", storageErr);
            techPack.ydoc.transact(() => { techPack.images.set(`chat_img_${fileMsgId}`, compressed); });
          }
        });
      }
      const base64Data = dataUrl.split(",")[1] ?? "";
      const fileType = file.type || (file.name.split(".").pop() ?? "binary");
      try {
        const uploadToken = await getFreshToken() ?? "";
        const uploadRes = await fetch("/api/upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(uploadToken ? { Authorization: `Bearer ${uploadToken}` } : {}),
          },
          body: JSON.stringify({ fileName: file.name, fileType, base64Data }),
        });
        if (uploadRes.ok) {
          const ctx = await uploadRes.json() as FileCtx;
          setFileCtx(ctx);
          const isZh = (languageCode ?? "en").startsWith("zh");

          if (ctx.type === "bom" && ctx.bomRows && ctx.bomRows.length > 0) {
            // BOM Excel: direct write to Yjs, no AI needed
            const lang = languageCode ?? "en";
            techPack.ydoc.transact(() => {
              for (const row of ctx.bomRows!) {
                const r = row as Record<string, unknown>;
                techPack.bom.push([{
                  id: `bom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                  component:   String(r["component"]   ?? r["部件"]  ?? r["Component"]  ?? ""),
                  material:    String(r["material"]    ?? r["物料"]  ?? r["Material"]   ?? ""),
                  composition: String(r["composition"] ?? r["成分"]  ?? r["Composition"] ?? ""),
                  gsm:         String(r["gsm"]         ?? r["克重"]  ?? r["GSM"]        ?? ""),
                  color_code:  String(r["color_code"]  ?? r["颜色"]  ?? r["Color"]      ?? ""),
                  fabric_code: String(r["fabric_code"] ?? r["面料编码"] ?? r["Fabric Code"] ?? ""),
                  width:       String(r["width"]       ?? r["幅宽"]  ?? r["Width"]      ?? ""),
                  price:       String(r["price"]       ?? r["价格"]  ?? r["Price"]      ?? ""),
                }]);
              }
            });
            const count = ctx.bomRows.length;
            addActivity({ icon: "📥", text: `BOM表格已映射 ${count} 行到物料清单`, ts: Date.now() });
            pushMsg({ id: `ai-bom-${Date.now()}`, authorId: "ai-assistant", authorName: "AI",
              text: `✓ 检测到BOM表格，已自动映射 ${count} 行到物料清单`, ts: Date.now(), msgType: "text" });
            window.dispatchEvent(new CustomEvent("studio:dataImported"));

          } else if (ctx.type === "image" && ctx.base64) {
            // Image: send multimodal request to AI
            const autoPrompt = isZh
              ? `请分析这张服装图片（${file.name}）并填写相关的 Tech Pack 字段。`
              : `Please analyze this garment/fabric image (${file.name}) and fill in the relevant Tech Pack fields.`;
            void sendToAI(autoPrompt, ctx);

          } else if (ctx.type === "techpack_json" && ctx.data) {
            // TechPack JSON: direct field mapping across all Y.Maps, no AI needed
            console.log("[handleFile] techpack_json path — data keys:", Object.keys(ctx.data as object));
            const CAMEL: Record<string, string> = {
              styleNumber: "style_number", productCategory: "product_category",
              projectName: "project_name", project_title: "project_name",
              seamAllowance: "seam_allowance",
              productionType: "production_type", specialTechniques: "special_techniques",
              hardwareTrim: "hardware_trim", additionalNotes: "additional_notes",
              factoryNotes: "factory_notes",
            };
            const data = ctx.data as Record<string, unknown>;
            const lang = languageCode ?? "en";
            let count = 0;
            const writeToBom = (material: string) => {
              const bomArray = techPack.bom;
              if (bomArray.length === 0) {
                bomArray.push([{ id: `bom-${Date.now()}`, component: 'Main Body', material, composition: '', gsm: '', color_code: '', fabric_code: '', width: '', price: '' }]);
              } else {
                const firstRow = bomArray.get(0) as BomRow;
                bomArray.delete(0, 1);
                bomArray.insert(0, [{ ...firstRow, material }]);
              }
            };
            techPack.ydoc.transact(() => {
              for (const [rawKey, val] of Object.entries(data)) {
                if (rawKey === "materials" && typeof val === "object" && val !== null) {
                  for (const [mk, mv] of Object.entries(val as Record<string, unknown>)) {
                    if (mv === null || mv === undefined || mv === '') continue;
                    const strMv = String(mv);
                    if (MATERIAL_FIELDS.has(mk)) { writeToBom(strMv); count++; console.log(`[techpack_json] materials.${mk} = "${strMv}" → bom.material`); }
                    else console.warn(`[techpack_json] materials.${mk} not in MATERIAL_FIELDS — DROPPED`);
                  }
                  continue;
                }
                if (rawKey === "measurements" && typeof val === "object" && val !== null && !Array.isArray(val)) {
                  for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
                    if (v === null || v === undefined || v === '') continue;
                    const env = makeField(String(v), lang, userId);
                    if (MEASUREMENT_KEYS.has(k)) { techPack.measurements.set(k, env); count++; }
                    else console.warn(`[techpack_json] measurements.${k} not in MEASUREMENT_KEYS — DROPPED`);
                  }
                  continue;
                }
                if (rawKey === "bom" && Array.isArray(val)) {
                  techPack.bom.delete(0, techPack.bom.length);
                  techPack.bom.push((val as Record<string, unknown>[]).map((row, i) => ({
                    id: `bom-${Date.now()}-${i}`,
                    component: String(row.component ?? ''), material: String(row.material ?? ''),
                    composition: String(row.composition ?? ''), gsm: String(row.gsm ?? ''),
                    color_code: String(row.color ?? row.color_code ?? ''),
                    fabric_code: String(row.fabric_code ?? ''), width: String(row.width ?? ''), price: String(row.price ?? ''),
                  })));
                  count += (val as unknown[]).length;
                  continue;
                }
                if (rawKey === "size_range" && Array.isArray(val)) {
                  const env = makeField((val as string[]).join(","), lang, userId);
                  techPack.metadata.set("size_range", env);
                  count++; continue;
                }
                const field = CAMEL[rawKey] ?? rawKey;
                const target = METADATA_FIELDS.has(field) ? "metadata"
                  : MATERIAL_FIELDS.has(field) ? "bom.material"
                  : CONSTRUCTION_FIELDS.has(field) ? "construction_details"
                  : MEASUREMENT_KEYS.has(field) ? "measurements"
                  : null;
                if (val === null || val === undefined || val === '') {
                  console.warn(`[techpack_json] key="${rawKey}" field="${field}" SKIPPED — val:`, val);
                  continue;
                }
                const strVal = String(val);
                if (!target) { console.warn(`[techpack_json] field="${field}" DROPPED — not in any field set`); continue; }
                console.log(`[techpack_json] key="${rawKey}" → field="${field}" → ${target} = "${strVal}"`);
                const env = makeField(strVal, lang, userId);
                if (METADATA_FIELDS.has(field))          { techPack.metadata.set(field, env); count++; }
                else if (MATERIAL_FIELDS.has(field))     { writeToBom(strVal); count++; }
                else if (CONSTRUCTION_FIELDS.has(field)) { techPack.construction_details.set(field, env); count++; }
                else if (MEASUREMENT_KEYS.has(field))    { techPack.measurements.set(field, env); count++; }
              }
            });
            console.log(`[techpack_json] total mapped: ${count}`);
            addActivity({ icon: "📥", text: `Tech Pack JSON 已映射 ${count} 个字段`, ts: Date.now() });
            pushMsg({ id: `ai-json-${Date.now()}`, authorId: "ai-assistant", authorName: "AI",
              text: `✓ 检测到Tech Pack JSON，已自动映射 ${count} 个字段`, ts: Date.now(), msgType: "text" });
            window.dispatchEvent(new CustomEvent("studio:dataImported"));

          } else {
            // PDF / docx / text: auto-trigger AI with extracted text
            const charCount = ctx.extractedText.length;
            if (charCount > 0) {
              addActivity({ icon: "📄", text: `${file.name} 已提取 ${charCount} 字符`, ts: Date.now() });
            }
            const autoPrompt = isZh
              ? `请分析这份文件（${file.name}，共 ${charCount} 字符）并填写相关的 Tech Pack 字段。`
              : `Please analyze the uploaded file (${file.name}, ${charCount} chars) and fill in the relevant Tech Pack fields.`;
            void sendToAI(autoPrompt, ctx);

            // Auto-map JSON fields directly
            if (fileType === "application/json" || file.name.endsWith(".json")) {
              try {
                const data = JSON.parse(atob(base64Data)) as Record<string, unknown>;
                const lang = languageCode ?? "en";
                let count = 0;
                techPack.ydoc.transact(() => {
                  for (const [key, val] of Object.entries(data)) {
                    if (val === null || val === undefined || val === '') continue;
                    const strVal = String(val);
                    const env = makeField(strVal, lang, userId);
                    if (METADATA_FIELDS.has(key)) { techPack.metadata.set(key, env); count++; }
                    else if (MATERIAL_FIELDS.has(key)) {
                      const bomArray = techPack.bom;
                      if (bomArray.length === 0) {
                        bomArray.push([{ id: `bom-${Date.now()}`, component: 'Main Body', material: strVal, composition: '', gsm: '', color_code: '', fabric_code: '', width: '', price: '' }]);
                      } else {
                        const firstRow = bomArray.get(0) as BomRow;
                        bomArray.delete(0, 1);
                        bomArray.insert(0, [{ ...firstRow, material: strVal }]);
                      }
                      count++;
                    }
                  }
                });
                if (count > 0) {
                  addActivity({ icon: "📥", text: `${file.name} 已映射 ${count} 个字段到表单`, ts: Date.now() });
                }
              } catch { /* not a field-mappable JSON */ }
            }
          }
        } else {
          const errText = await uploadRes.text();
          console.error("[upload] server error:", uploadRes.status, errText);
          pushMsg({
            id: `err-${Date.now()}`,
            authorId: "ai-assistant",
            authorName: "AI",
            text: `⚠️ 上传失败（${uploadRes.status}）：${errText.slice(0, 100)}`,
            ts: Date.now(),
            msgType: "text",
          });
        }
      } catch (err) {
        console.error("[upload] failed:", err);
        setError(`⚠️ 文件上传失败：${err instanceof Error ? err.message : "网络错误"}，请重试。`);
        setFileCtx(null);
      }
    };
    reader.readAsDataURL(file);
  }, [pushMsg, userId, userName, languageCode, techPack, addActivity, sendToAI, compressImage]);

  const activeModel = CHAT_MODELS.find(m => m.id === chatModel) ?? CHAT_MODELS[0];

  // msg.authorColor (set from DB on reload, or userColor on send) takes priority;
  // falls back to live awareness lookup (connected users) then a neutral gray.
  function resolveColor(authorId: string, explicit?: string): string {
    if (explicit) return explicit;
    if (authorId === "ai-assistant") return "#5b6af0";
    if (authorId === userId) return userColor;
    return awarenessUsers.find(u => u.userId === authorId)?.color ?? "#a0a0a0";
  }

  function fmtMsgTime(ts: number): string {
    const opts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
    try {
      return new Intl.DateTimeFormat([], { ...opts, timeZone: userTimezone }).format(new Date(ts));
    } catch {
      return new Date(ts).toLocaleTimeString([], opts);
    }
  }

  const ICON_BTN: React.CSSProperties = {
    background: "transparent", border: "none", cursor: "pointer",
    color: "#6e6e6e", fontSize: 15, padding: "0 3px", lineHeight: 1, flexShrink: 0,
  };
  const canSend = draft.trim() && !streaming;
  const SEND_BTN: React.CSSProperties = {
    background: canSend ? "#ffffff" : "transparent",
    border: "1px solid " + (canSend ? "#ffffff" : "#3a3a3a"),
    color: canSend ? "#000000" : "#3a3a3a",
    fontFamily: "var(--font)", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
    padding: "6px 10px", cursor: canSend ? "pointer" : "default",
    flexShrink: 0, whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Tab bar */}
      <div className="sf-tabs">
        <button className={`sf-tab${activeTab === "chat" ? " active" : ""}`} onClick={() => setActiveTab("chat")}>{tabLabel("chat", languageCode)}</button>
        <button className={`sf-tab${activeTab === "activity" ? " active" : ""}`} onClick={() => setActiveTab("activity")}>{tabLabel("activity", languageCode)}</button>
      </div>

      {/* Scrollable content area */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "12px" }}>
        {activeTab === "chat" ? (
          <>
            {messages.length === 0 && !streaming ? (
              <div className="sf-empty-state">NO MESSAGES YET</div>
            ) : (
              <>
                {messages.map(msg => {
                  if (msg.msgType === "file") {
                    const imgSrc = imgCacheRef.current.get(msg.id) ?? techPack.images.get(`chat_img_${msg.id}`);
                    if (imgSrc) {
                      return (
                        <div key={msg.id} style={{ marginBottom: 10 }}>
                          <img src={imgSrc} alt={msg.fileName} style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 4, display: "block", border: "1px solid #2a2a2a" }} />
                          <div style={{ fontSize: 9, color: "#3a3a3a", marginTop: 3 }}>{msg.fileName}</div>
                        </div>
                      );
                    }
                    return (
                      <div key={msg.id} className="sf-file-card" style={{ marginBottom: 10 }}>
                        <span style={{ fontSize: 16 }}>{fileIcon(msg.fileType, msg.fileName)}</span>
                        <span className="sf-file-card-name">{msg.fileName ?? msg.text}</span>
                      </div>
                    );
                  }
                  const isAI = msg.authorId === "ai-assistant";
                  const msgColor = resolveColor(msg.authorId, msg.authorColor);
                  const unsaved = failedMsgIds.has(msg.id);
                  return (
                    <div key={msg.id} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                        {!isAI && <span style={{ width: 7, height: 7, borderRadius: "50%", background: msgColor, flexShrink: 0, display: "inline-block" }} />}
                        <span style={{ fontSize: 11, fontWeight: 700, color: isAI ? "#5b6af0" : msgColor }}>
                          {msg.authorName}
                        </span>
                        <span style={{ fontSize: 9, color: "#3a3a3a" }}>{fmtMsgTime(msg.ts)}</span>
                        {unsaved && <span style={{ fontSize: 9, color: "#e05555", marginLeft: 2 }}>⚠ 未保存</span>}
                      </div>
                      <div style={{ fontSize: 11, color: isAI ? "#9090cc" : "#6e6e6e", lineHeight: 1.6, wordBreak: "break-word" }}>
                        {isAI ? cleanMarkdown(stripFieldTags(msg.text)) : msg.text}
                      </div>
                      {unsaved && (
                        <button
                          onClick={() => void persistMessage(msg.id, msg.text)}
                          style={{ background: "none", border: "none", color: "#e05555", cursor: "pointer", fontSize: 9, padding: 0, marginTop: 2, fontFamily: "var(--font)" }}
                        >
                          重试
                        </button>
                      )}
                    </div>
                  );
                })}
                {streaming && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#5b6af0" }}>AI</span>
                      <span style={{ fontSize: 9, color: "#3a3a3a" }}>now</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#9090cc", lineHeight: 1.6, wordBreak: "break-word" }}>
                      {streamText ? cleanMarkdown(streamText.replace(/<FIELD_UPDATE>[\s\S]*?(<\/FIELD_UPDATE>|$)/g, "").trim()) : <span style={{ opacity: 0.5 }}>▋</span>}
                    </div>
                  </div>
                )}
              </>
            )}
            {error && <div style={{ fontSize: 10, color: "#e05555", padding: "6px 0", fontFamily: "var(--font)", lineHeight: 1.5 }}>⚠ {error}</div>}
            <div ref={bottomRef} />
          </>
        ) : (
          <>
            {/* TP-Layer Activity Section = PACK-CONTENT events for THIS pack only
                (governance §0a). Folder-governance events live at the Surface
                Layer (landing), never here. roomId = the current pack id. */}
            <FolderActivityStream scope="pack" id={roomId} lang={languageCode} />
            <div ref={actBottomRef} />
          </>
        )}
      </div>

      {/* ── Input area — always visible regardless of active tab ── */}

      {/* Row 1: Model selector (SpecFlow layout: STUDIO | MODEL | [dropdown]) */}
      <div className="sf-model-row">
        <div className="sf-model-sel-wrap">
          <span className="sf-engine-tag">STUDIO</span>
          <span className="sf-model-sep">|</span>
          <span className={`sf-engine-badge${activeModel.id.startsWith("gemini") ? " gemini" : ""}`}>{activeModel.shortLabel}</span>
          <span
            title={`${activeModel.label}: ${modelHealth[chatModel]?.status ?? "ok"}`}
            style={{ fontSize: 8, color: healthDotColor(modelHealth, chatModel), lineHeight: 1, flexShrink: 0 }}
          >●</span>
          <span className="sf-model-sep">|</span>
          <select
            value={chatModel}
            onChange={e => { setChatModel(e.target.value as ChatModelId); localStorage.setItem("studio_chat_model", e.target.value); }}
            className="sf-model-sel"
          >
            {CHAT_MODELS.map(m => (
              <option key={m.id} value={m.id}>
                {m.label}{m.byok ? " (BYOK)" : ""}{modelHealth[m.id]?.status === "down" ? " ✗" : modelHealth[m.id]?.status === "degraded" ? " ⚠" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="sf-model-disclaimer">Output quality varies by model.</div>
      </div>

      {/* Row 2: Message input + send */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px 4px", flexShrink: 0 }}>
        <input
          type="text" value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={streaming ? "AI is thinking…" : "Message…"}
          disabled={streaming}
          className="sf-chat-input"
          style={{ flex: 1 }}
        />
        <button onClick={send} disabled={!canSend} style={SEND_BTN}>发送</button>
      </div>

      {/* Row 3: Toolbar (upload + voice) */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 12px 10px", flexShrink: 0 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf,application/json,.json,.xlsx,.xls,.docx"
          style={{ display: "none" }}
          onChange={handleFile}
        />
        <button onClick={() => fileInputRef.current?.click()} title="Upload file" aria-label="Upload file" style={ICON_BTN}>
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M7 3h7l4 4v14H7z"/><path d="M14 3v5h5M10 13h5M10 17h5"/></svg>
        </button>
        <button disabled aria-label="Voice input unavailable" title="Voice input — coming soon" style={{ ...ICON_BTN, cursor: "not-allowed", color: "#8a8a8a", opacity: 0.55 }}>
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8"/></svg>
        </button>
        {fileContext && (
          <span style={{ fontSize: 9, color: "#4a4a6a", fontFamily: "var(--font)", letterSpacing: "0.05em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={`${fileContext.extractedText.length} chars extracted`}>
            {fileIcon(fileContext.fileType, fileContext.fileName)} {fileContext.fileName}
            {fileContext.extractedText.length > 0 && <span style={{ color: "#3a3a5a" }}> ({fileContext.extractedText.length}ch)</span>}
          </span>
        )}
      </div>

    </div>
  );
}
